import * as React from 'react';
import * as Sentry from '@sentry/browser';
import { connect } from 'react-redux';
import isNil from 'lodash/isNil';
import isEqual from 'lodash/isEqual';
import moment from 'moment';

import ActionButton from 'components/buttons/action-button/ActionButton';
import Tooltip from 'components/Tooltip';
import { withModal } from 'components/modal/ModalHOC';
import PromptModal from 'components/modal/prompt-modal/PromptModal';

import RealmProfileMatchModal from '../realm-profile-match-modal/RealmProfileMatchModal';
import OutfitReviewInfo from '../outfit-review-info/OutfitReviewInfo';
import OutfitReviewHeader from '../outfit-review-header/OutfitReviewHeader';
import RealmOutfitDetails from '../realm-outfit-details/RealmOutfitDetails';
import RealmOutfitDetailsEdit from '../realm-outfit-details-edit/RealmOutfitDetailsEdit';

import { __t, applyTranslations } from 'utils/language';
import {
  CAN_PUBLISH_STATUSES,
  ITEMS_PER_OUTFIT,
  REALM_OUTFIT_STATUS_PENDING,
  getOutfitsWithoutDraft,
  getValidationError,
  shouldVideoPipelineBeRetriggered,
  splitTagsByType
} from 'utils/realm-outfits';

import { danger } from 'redux-store/reducers/common-reducer';
import { hideConfirmModal, showConfirmModal } from 'redux-store/reducers/modal-reducer';
import {
  declineRealmOutfit,
  publishRealmOutfit,
  reTriggerVideoPipeline,
  unPublishRealmOutfit,
  updateRealmOutfit
} from 'modules/realm-outfits/reducers/realm-outfits';
import {
  getMatchesOfRealmProfileWithEMSProfiles,
  matchRealmProfileWithEMSProfile
} from 'modules/realm-outfits/reducers/admin-realm-profiles';

import './outfit-review-modal.scss';

applyTranslations(require('./outfit-review-modal.i18n.json'));
applyTranslations(require('../realm-outfit-review/realm-outfit-review.i18n.json'));

import { Campaign, Deliverable, RealmOutfit, RealmOutfitForm } from 'types';
import {
  RealmProfile,
  RealmProfileEMSResource,
  RealmProfileEMSResourceMatches
} from '../../types/RealmProfile.type';

type OwnProps = {
  campaign: Campaign,
  outfitPosition: number,
  deliverableId: number,
  offerId: number,
  creatorWmcLink: string | null,
  onCloseModal: () => void,
  pageType?: 'offer' | 'content', // eslint-disable-line react/no-unused-prop-types
  influencerName?: string | null
};

type StateProps = {
  outfits: Array<RealmOutfit>,
  realmOutfitAudioTypeEnabled: boolean
}

type DispatchProps = {
  dispatchDeclineOutfit: (outfitId: number, declinedReason: string, deliverableId: number, offerId: number) => void,
  dispatchPublishOutfit: (outfitId: number, deliverableId: number, offerId: number) => void,
  dispatchUpdateRealmOutfit: (outfit: RealmOutfitForm, deliverableId: number, offerId: number) => Promise<void>,
  dispatchUnpublishOutfit: (outfitId: number, deliverableId, offerId: number) => void,
  dispatchRetriggerVideoPipeline: (deliverableId: number, outfitId: number) => void,
  dispatchShowConfirmModal: (props: {
    message: string,
    onConfirm: () => void,
    onCancel: () => void
  }) => Promise<void>,
  dispatchHideConfirmModal: () => Promise<void>,
  dispatchMatchRealmProfileWithEMSProfile: (influencerId: number, emsId?: string) => (null | RealmProfile),
  dispatchGetMatchesOfRealmProfileWithEMSProfiles: (influencerId: number) => RealmProfileEMSResourceMatches,
  dispatchInvalidPublishDates: () => void
}

type State = {
  outfitPosition: number | null,
  isEditMode: boolean,
  emsMatches: null | Array<RealmProfileEMSResource>
};

type Props = OwnProps & StateProps & DispatchProps;

const ActionButtonWithModal = withModal(ActionButton, {
  modalContent: PromptModal
});

const msgKey = 'outfit_review_modal';

class OutfitReviewModal extends React.Component<Props, State> {
  state = {
    outfitPosition: 0,
    isEditMode: false,
    emsMatches: null
  };

  componentDidMount() {
    const { outfitPosition } = this.props;

    this.setState({
      outfitPosition
    });
  }

  shouldComponentUpdate(nextProps: Readonly<Props>, nextState: Readonly<State>): boolean {
    return !isEqual(nextProps, this.props)
      || !isEqual(nextState, this.state);
  }

  getCurrentOutfit = (): RealmOutfit => {
    const { outfits } = this.props;
    const { outfitPosition } = this.state;

    return outfits[outfitPosition];
  };

  handleEditOutfit = () => {
    this.setState(prevState => ({
      ...prevState,
      isEditMode: true
    }));
  };

  handlePublish = async () => {
    const {
      dispatchPublishOutfit,
      dispatchMatchRealmProfileWithEMSProfile,
      dispatchGetMatchesOfRealmProfileWithEMSProfiles,
      dispatchInvalidPublishDates
    } = this.props;
    const outfit = this.getCurrentOutfit();
    const profile = await dispatchMatchRealmProfileWithEMSProfile(outfit.influencerId);

    if (profile === null) {
      const data = await dispatchGetMatchesOfRealmProfileWithEMSProfiles(outfit.influencerId);

      if (data.matches.length > 0) {
        this.setState({ emsMatches: data.matches });
      }
    } else if (moment().diff(moment(outfit.publishedFrom), 'days') > 0) {
      dispatchInvalidPublishDates();
    } else {
      this.showConfirmationModal(
        __t(`${msgKey}.confirmation_publish_message`),
        (outfitId, deliverableId, offerId) => dispatchPublishOutfit(outfitId, deliverableId, offerId)
      );
    }
  };

  handleRetriggerPipeline = () => {
    const { deliverableId, onCloseModal, dispatchRetriggerVideoPipeline } = this.props;

    const outfit = this.getCurrentOutfit();

    dispatchRetriggerVideoPipeline(deliverableId, outfit.id);

    onCloseModal();

    window.location.reload();
  };

  handleDecline = (declinedReason: string) => {
    const { deliverableId, offerId, dispatchDeclineOutfit } = this.props;
    const outfit = this.getCurrentOutfit();

    dispatchDeclineOutfit(outfit.id, declinedReason, deliverableId, offerId);
  };

  handleUnpublish = () => {
    const {
      dispatchUnpublishOutfit
    } = this.props;

    this.showConfirmationModal(
      __t('realm_outfit_details.confirmation_unpublish.message'),
      (outfitId, deliverableId, offerId) => dispatchUnpublishOutfit(outfitId, deliverableId, offerId)
    );
  };

  handleSave = async (outfit: RealmOutfitForm) => {
    const { deliverableId, offerId, dispatchUpdateRealmOutfit } = this.props;

    await dispatchUpdateRealmOutfit(outfit, deliverableId, offerId);

    this.setState({
      isEditMode: false
    });
  };

  handleCancel = () => {
    this.setState({
      isEditMode: false
    });
  };

  showConfirmationModal = (
    message: string,
    action: (outfitId: number, deliverableId: number, offerId: number) => void
  ) => {
    const {
      deliverableId,
      offerId,
      dispatchShowConfirmModal,
      dispatchHideConfirmModal
    } = this.props;
    const outfit = this.getCurrentOutfit();

    dispatchShowConfirmModal({
      message,
      onConfirm: () => {
        action(outfit.id, deliverableId, offerId);

        dispatchHideConfirmModal();
      },
      onCancel: () => dispatchHideConfirmModal()
    });
  };

  renderMatchingModal = () => {
    const {
      dispatchPublishOutfit,
      dispatchMatchRealmProfileWithEMSProfile
    } = this.props;
    const { emsMatches } = this.state;
    const DivWithMatchingModal = withModal(<div />, {
      modalContent: RealmProfileMatchModal,
      autoOpenModal: true,
      onModalClosed: () => this.setState({ emsMatches: null })
    });
    const outfit = this.getCurrentOutfit();

    return (
      <DivWithMatchingModal
        modalContentProps={{
          matches: emsMatches,
          onSubmit: async (emsId: string) => {
            const profile = await dispatchMatchRealmProfileWithEMSProfile(outfit.influencerId, emsId);

            if (isNil(profile)) {
              Sentry.captureException(new Error(__t(`${msgKey}.match_error`)));

              return false;
            }

            this.showConfirmationModal(
              __t(`${msgKey}.confirmation_publish_message`),
              (outfitId, deliverableId, offerId) => dispatchPublishOutfit(outfitId, deliverableId, offerId)
            );

            return true;
          }
        }}
      />
    );
  };

  renderActionButtons = (retriggerVideoError: string) => {
    const { isEditMode } = this.state;
    const { realmOutfitAudioTypeEnabled } = this.props;
    const outfit: RealmOutfit = this.getCurrentOutfit();
    const tags = splitTagsByType(outfit.tags);
    const validationErrorKey = getValidationError(outfit, tags.gtl, tags.regular, realmOutfitAudioTypeEnabled);
    const validationError = __t(`realm_outfit_review.errors.${validationErrorKey}`, {
      minItems: ITEMS_PER_OUTFIT.get().min,
      maxItems: ITEMS_PER_OUTFIT.get().max
    });

    const confirmationButton = (
      <ActionButton
        color="primary"
        disabled={Boolean(validationErrorKey) || !!retriggerVideoError}
        onClick={() => this.handlePublish()}
      >
        {__t(`${msgKey}.buttons.publish`)}
      </ActionButton>
    );

    if (!isEditMode) {
      return (
        <React.Fragment>
          {
            outfit.status === REALM_OUTFIT_STATUS_PENDING && (
              <ActionButtonWithModal
                color="secondary"
                modalContentProps={{
                  onSubmit: this.handleDecline,
                  title: __t('realm_outfit_review.confirmation_decline.message'),
                  label: __t('realm_outfit_review.confirmation_decline.reason_label'),
                  value: '',
                  multiline: true,
                  okBtnText: __t('realm_outfit_review.confirmation_decline.button')
                }}
              >
                {__t(`${msgKey}.buttons.decline`)}
              </ActionButtonWithModal>
            )
          }
          {
            (validationErrorKey || retriggerVideoError) ?
              <Tooltip message={validationError || retriggerVideoError}>
                {confirmationButton}
              </Tooltip> :
              confirmationButton
          }
        </React.Fragment>
      );
    }

    return null;
  };

  render() {
    const { campaign, outfits, influencerName, deliverableId, creatorWmcLink } = this.props;
    const { outfitPosition, isEditMode, emsMatches } = this.state;

    if (outfits.length === 0 || outfitPosition === null) {
      return null;
    }

    const outfit = outfits[outfitPosition];
    const pipelineStatus = outfit.video?.pipelineStatus;

    const needToRetriggerVideoPipeline = outfit.video ?
      shouldVideoPipelineBeRetriggered(
        outfit.video?.pipelineStatus ?? null,
        outfit.video?.thumbnailUrl ?? null
      ) : false;

    return (
      <React.Fragment>
        <div className="outfit-review-modal__container">
          <OutfitReviewHeader
            title={outfit.name}
            subTitle={influencerName}
            currentPosition={outfitPosition}
            maxPosition={outfits.length - 1}
            isEditMode={isEditMode}
            onChangingCurrentPosition={(position) => {
            // To have animation effect, outfit position set to null
              this.setState({
                outfitPosition: null
              }, () => {
                this.setState({
                  outfitPosition: position
                });
              });
            }}
          />
          <div className="outfit-review-modal__outfit-info">
            <OutfitReviewInfo
              creatorWmcLink={creatorWmcLink}
              outfit={outfit}
              onEditOutfit={!isEditMode ? this.handleEditOutfit : null}
              onUnpublishOutfit={this.handleUnpublish}
            />
          </div>
          {
            isEditMode ? (
              <RealmOutfitDetailsEdit
                campaign={campaign}
                outfit={outfit}
                isBrand
                onSave={this.handleSave}
                onCancel={this.handleCancel}
              />
            ) : (
              <div className="outfit-review-modal__outfit-details">
                <RealmOutfitDetails
                  hasProcessingCompleted={pipelineStatus !== 'UPLOAD_INIT'}
                  onRetriggerVideoProcessing={
                    needToRetriggerVideoPipeline
                      ? this.handleRetriggerPipeline
                      : undefined
                  }
                  deliverableId={deliverableId}
                  outfit={outfit}
                  isBrand
                />
              </div>
            )
          }
          {
            !isEditMode && CAN_PUBLISH_STATUSES.includes(outfit.status) && (
              <div className="outfit-review-modal__outfit-action">
                {this.renderActionButtons(needToRetriggerVideoPipeline ? __t('outfit_review_modal.retrigger_video_error') : '')}
              </div>
            )
          }
        </div>
        <div className="outfit-review-modal__empty-container" />
        {emsMatches ? this.renderMatchingModal() : null}
      </React.Fragment>
    );
  }
}

const mapStateToProps = ({ offerDeliverables, campaignDeliverables, runtimeConfig }, { offerId, deliverableId, pageType }): StateProps => {
  let outfits: Array<RealmOutfit> = [];
  let deliverables: Array<Deliverable> = [];

  if (pageType === 'offer') {
    deliverables = offerDeliverables.deliverablesByOfferId[offerId];
  }

  if (pageType === 'content') {
    deliverables = campaignDeliverables.deliverables;
  }

  if (deliverables && deliverables.length > 0) {
    const currentDeliverable = deliverables.find(deliverable => deliverable.id === deliverableId);

    outfits = currentDeliverable?.outfits || [];
  }

  return {
    outfits: getOutfitsWithoutDraft(outfits),
    realmOutfitAudioTypeEnabled: runtimeConfig.config.realmOutfitAudioTypeEnabled ?? false
  };
};

const mapDispatchToProps = (dispatch, { pageType }): DispatchProps => ({
  dispatchDeclineOutfit: (outfitId, _declinedReason, deliverableId, offerId) =>
    dispatch(declineRealmOutfit(outfitId, _declinedReason, deliverableId, offerId, pageType)),
  dispatchPublishOutfit: (outfitId, deliverableId, offerId) =>
    dispatch(publishRealmOutfit(outfitId, deliverableId, offerId, pageType)),
  dispatchRetriggerVideoPipeline: (deliverableId, outfitId) =>
    dispatch(reTriggerVideoPipeline(deliverableId, outfitId)),
  dispatchUnpublishOutfit: (outfitId, deliverableId, offerId) =>
    dispatch(unPublishRealmOutfit(outfitId, deliverableId, offerId, pageType)),

  dispatchShowConfirmModal: (props) => dispatch(showConfirmModal(props)),
  dispatchHideConfirmModal: () => dispatch(hideConfirmModal()),
  dispatchUpdateRealmOutfit: (outfit, deliverableId, offerId) => dispatch(
    updateRealmOutfit(outfit, deliverableId, offerId, pageType)
  ),
  dispatchMatchRealmProfileWithEMSProfile: (id, emsId) => dispatch(matchRealmProfileWithEMSProfile(id, emsId)),
  dispatchGetMatchesOfRealmProfileWithEMSProfiles: id => dispatch(getMatchesOfRealmProfileWithEMSProfiles(id)),
  dispatchInvalidPublishDates: () => dispatch(danger(__t('outfit_review_modal.published_dates_invalid')))
});

export default connect(mapStateToProps, mapDispatchToProps)(OutfitReviewModal);
